<template>
    <div v-if="showWidget" class="sipinor-ai-widget">
        <transition name="sipinor-ai-fade">
            <div
                v-show="panelOpen"
                class="sipinor-ai-panel"
                :class="[
                    { 'sipinor-ai-panel--dark': isDark, 'sipinor-ai-panel--expanded': panelExpanded },
                    mode === 'action' ? 'sipinor-ai-panel--mode-action' : 'sipinor-ai-panel--mode-suggestion',
                ]"
                role="dialog"
                aria-label="AI assistant"
            >
                <div class="sipinor-ai-panel__head">
                    <div class="sipinor-ai-panel__brand">
                        <div class="sipinor-ai-panel__avatar" aria-hidden="true">
                            <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 1 1-8 0V6a4 4 0 0 1 4-4z" />
                                <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                            </svg>
                        </div>
                        <div class="sipinor-ai-panel__titles">
                            <div class="sipinor-ai-panel__title-row">
                                <span class="sipinor-ai-panel__title">{{ trans('configuration.ai_assistant') }}</span>
                                <span
                                    class="sipinor-ai-panel__mode-chip"
                                    :class="mode === 'action' ? 'sipinor-ai-panel__mode-chip--action' : 'sipinor-ai-panel__mode-chip--suggestion'"
                                >
                                    {{ mode === 'action' ? trans('configuration.ai_assistant_action_mode') : trans('configuration.ai_assistant_suggestion_mode') }}
                                </span>
                            </div>
                            <div class="sipinor-ai-panel__subtitle">
                                {{
                                    mode === 'action'
                                        ? trans('configuration.ai_assistant_panel_subtitle_action')
                                        : trans('configuration.ai_assistant_panel_subtitle_suggestion')
                                }}
                            </div>
                        </div>
                    </div>
                    <div class="sipinor-ai-panel__head-actions">
                        <button
                            type="button"
                            class="sipinor-ai-panel__icon-btn sipinor-ai-panel__icon-btn--expand"
                            :title="panelExpanded ? trans('configuration.ai_assistant_shrink_panel') : trans('configuration.ai_assistant_expand_panel')"
                            :aria-label="panelExpanded ? trans('configuration.ai_assistant_shrink_panel') : trans('configuration.ai_assistant_expand_panel')"
                            @click="togglePanelExpanded"
                        >
                            <em
                                v-if="!panelExpanded"
                                class="icon ni ni-maximize"
                                aria-hidden="true"
                            ></em>
                            <em v-else class="icon ni ni-minimize" aria-hidden="true"></em>
                        </button>
                        <button
                            type="button"
                            class="sipinor-ai-panel__icon-btn"
                            :title="trans('configuration.ai_assistant_clear_chat')"
                            :aria-label="trans('configuration.ai_assistant_clear_chat')"
                            @click="startNewChat"
                        >
                            <em class="icon ni ni-trash"></em>
                        </button>
                        <button type="button" class="sipinor-ai-panel__icon-btn" :aria-label="trans('general.close')" @click="closePanel">
                            <em class="icon ni ni-cross"></em>
                        </button>
                    </div>
                </div>
                <div class="sipinor-ai-panel__toolbar">
                    <div class="sipinor-ai-segmented" role="group">
                        <button
                            type="button"
                            class="sipinor-ai-segmented__btn"
                            :class="{ 'is-active': mode === 'suggestion' }"
                            @click="setMode('suggestion')"
                        >
                            {{ trans('configuration.ai_assistant_suggestion_mode') }}
                        </button>
                        <button
                            type="button"
                            class="sipinor-ai-segmented__btn"
                            :class="{ 'is-active': mode === 'action' }"
                            @click="setMode('action')"
                        >
                            {{ trans('configuration.ai_assistant_action_mode') }}
                        </button>
                        <button
                            type="button"
                            class="sipinor-ai-segmented__btn sipinor-ai-segmented__btn--icon"
                            :title="trans('configuration.ai_assistant_history')"
                            @click.stop="toggleHistory"
                        >
                            <em class="icon ni ni-clock"></em>
                        </button>
                    </div>
                    <div v-if="showHistory" class="sipinor-ai-history-pop">
                        <button type="button" class="sipinor-ai-history-pop__new" @click="startNewChat">
                            {{ trans('configuration.ai_assistant_new_chat') }}
                        </button>
                        <div v-if="!threadHistory.length" class="sipinor-ai-history-pop__empty">
                            {{ trans('configuration.ai_assistant_no_saved_chats') }}
                        </div>
                        <div class="sipinor-ai-history-pop__note">
                            {{ trans('configuration.ai_assistant_history_device_note') }}
                        </div>
                        <button
                            v-for="h in threadHistory"
                            :key="h.id"
                            type="button"
                            class="sipinor-ai-history-item"
                            :title="h.title"
                            @click="loadThread(h)"
                        >
                            {{ h.title }}
                        </button>
                    </div>
                </div>
                <div class="sipinor-ai-panel__body">
                    <div ref="scrollArea" class="sipinor-ai-messages">
                        <div v-for="(m, i) in messages" :key="i" class="sipinor-ai-msg" :class="'sipinor-ai-msg--' + m.role">
                            <div
                                v-if="m.role === 'user'"
                                class="sipinor-ai-msg__bubble sipinor-ai-msg__bubble--user"
                                :class="{ 'sipinor-ai-msg__bubble--user-with-media': userAttachmentThumbs(m).length }"
                            >
                                <div v-if="userAttachmentThumbs(m).length" class="sipinor-ai-attach-strip sipinor-ai-attach-strip--sent">
                                    <button
                                        v-for="(src, j) in userAttachmentThumbs(m)"
                                        :key="j"
                                        type="button"
                                        class="sipinor-ai-attach-strip__hit"
                                        :title="trans('configuration.ai_assistant_image_preview')"
                                        @click="openAttachmentPreview(userAttachmentFullSrc(m, j) || src)"
                                    >
                                        <img
                                            :src="src"
                                            alt=""
                                            class="sipinor-ai-attach-strip__thumb"
                                            @load="scrollBottom"
                                        >
                                    </button>
                                </div>
                                <div v-if="userMessageDisplayText(m)" class="sipinor-ai-msg__text">{{ userMessageDisplayText(m) }}</div>
                                <div v-if="m.screenCapture" class="sipinor-ai-screen-badge">
                                    {{ trans('configuration.ai_assistant_screen_badge') }}
                                </div>
                            </div>
                            <div v-else class="sipinor-ai-msg__assistant-wrap">
                                <div class="sipinor-ai-msg__avatar-mini" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 1 1-8 0V6a4 4 0 0 1 4-4z" />
                                        <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                                    </svg>
                                </div>
                                <div
                                    class="sipinor-ai-msg__bubble sipinor-ai-msg__bubble--assistant sipinor-ai-msg__bubble--rich"
                                    v-html="formatAssistantHtml(m.content)"
                                ></div>
                            </div>
                        </div>
                        <div v-if="loading" class="sipinor-ai-msg sipinor-ai-msg--assistant">
                            <div class="sipinor-ai-msg__assistant-wrap">
                                <div class="sipinor-ai-msg__avatar-mini" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2">
                                        <path d="M12 2a4 4 0 0 1 4 4v1a4 4 0 1 1-8 0V6a4 4 0 0 1 4-4z" />
                                        <path d="M4 21v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
                                    </svg>
                                </div>
                                <div class="sipinor-ai-msg__bubble sipinor-ai-msg__bubble--assistant sipinor-ai-typing" aria-live="polite">
                                    <span class="sipinor-ai-typing__dot"></span>
                                    <span class="sipinor-ai-typing__dot"></span>
                                    <span class="sipinor-ai-typing__dot"></span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="showActionSuggestedPrompts && actionPromptLangKeys.length" class="sipinor-ai-action-prompts">
                        <p class="sipinor-ai-action-prompts__heading">{{ actionPromptsSectionTitle }}</p>
                        <div class="sipinor-ai-action-prompts__chips" role="list">
                            <button
                                v-for="key in actionPromptLangKeys"
                                :key="key"
                                type="button"
                                class="sipinor-ai-action-prompts__chip"
                                role="listitem"
                                :disabled="loading"
                                @click="selectActionPrompt(key)"
                            >
                                {{ actionPromptLabel(key) }}
                            </button>
                        </div>
                    </div>
                    <form class="sipinor-ai-compose" @submit.prevent="send()">
                        <input
                            ref="imageFileInput"
                            type="file"
                            class="sipinor-ai-file-input"
                            accept="image/jpeg,image/png,image/webp"
                            multiple
                            @change="onImageFilesSelected"
                        >
                        <div class="sipinor-ai-compose__field">
                            <div v-if="pendingImages.length" class="sipinor-ai-compose__attachments" role="group" :aria-label="trans('configuration.ai_assistant_pending_capture')">
                                <div v-for="img in pendingImages" :key="img.id" class="sipinor-ai-compose__attach-item">
                                    <button
                                        type="button"
                                        class="sipinor-ai-compose__attach-hit"
                                        :title="trans('configuration.ai_assistant_image_preview')"
                                        @click="openAttachmentPreview(img.previewDataUrl)"
                                    >
                                        <img
                                            :src="img.previewDataUrl"
                                            alt=""
                                            class="sipinor-ai-compose__attach-thumb"
                                            @load="scrollBottom"
                                        >
                                    </button>
                                    <button
                                        type="button"
                                        class="sipinor-ai-compose__attach-remove"
                                        :title="trans('general.remove')"
                                        @click.stop="removePendingImage(img.id)"
                                    >
                                        <em class="icon ni ni-cross"></em>
                                    </button>
                                </div>
                            </div>
                            <textarea
                                ref="draftInput"
                                :value="draft"
                                class="sipinor-ai-textarea"
                                rows="2"
                                maxlength="4000"
                                :placeholder="trans('configuration.ai_assistant_placeholder')"
                                :disabled="loading"
                                autocomplete="off"
                                @input="onDraftInput"
                                @keydown="onDraftKeydown"
                            ></textarea>
                            <div class="sipinor-ai-compose__actions">
                                <button
                                    type="button"
                                    class="sipinor-ai-icon-btn"
                                    :disabled="loading || capturingScreen || pendingImages.length >= 5"
                                    :title="trans('configuration.ai_assistant_capture_screen')"
                                    @click="captureScreen"
                                >
                                    <em class="icon ni ni-camera"></em>
                                </button>
                                <button
                                    type="button"
                                    class="sipinor-ai-icon-btn"
                                    :disabled="loading || pendingImages.length >= 5"
                                    :title="trans('configuration.ai_assistant_attach_images')"
                                    @click="openImagePicker"
                                >
                                    <em class="icon ni ni-upload"></em>
                                </button>
                                <button
                                    class="sipinor-ai-send"
                                    type="submit"
                                    :disabled="loading || (!canSendContent && !pendingImages.length)"
                                >
                                    <span>{{ trans('configuration.ai_assistant_send') }}</span>
                                    <em class="icon ni ni-send"></em>
                                </button>
                            </div>
                        </div>
                        <p class="sipinor-ai-compose__kbd-hint">{{ trans('configuration.ai_assistant_multiline_hint') }}</p>
                    </form>
                </div>
            </div>
        </transition>
        <button
            type="button"
            class="sipinor-ai-fab"
            :class="{
                'sipinor-ai-fab--open': panelOpen,
                'sipinor-ai-fab--action': mode === 'action',
            }"
            :title="trans('configuration.ai_assistant')"
            @click="toggleFab"
        >
            <em class="icon ni ni-chat-circle" v-if="!panelOpen"></em>
            <em class="icon ni ni-cross" v-else></em>
        </button>
        <div
            v-if="attachmentPreviewSrc"
            class="sipinor-ai-lightbox"
            role="dialog"
            :aria-label="trans('configuration.ai_assistant_image_preview')"
        >
            <div class="sipinor-ai-lightbox__backdrop" @click="closeAttachmentPreview"></div>
            <div class="sipinor-ai-lightbox__frame" @click.stop>
                <button
                    type="button"
                    class="sipinor-ai-lightbox__close"
                    :title="trans('configuration.ai_assistant_close_preview')"
                    :aria-label="trans('configuration.ai_assistant_close_preview')"
                    @click="closeAttachmentPreview"
                >
                    <em class="icon ni ni-cross"></em>
                </button>
                <img :src="attachmentPreviewSrc" alt="" class="sipinor-ai-lightbox__img">
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex';
import html2canvas from 'html2canvas';

/**
 * Read ordered paths: navigate_chain (preferred), else navigate_to, else regex on assistant text.
 *
 * @returns {string[]}
 */
function extractNavigatePathsFromApi(body, assistantRawMessage) {
    const paths = [];
    const pushPath = (v) => {
        if (v == null || v === '') {
            return;
        }
        const s = String(v).trim();
        if (s.charAt(0) === '/') {
            paths.push(s);
        }
    };
    const addChainOrSingle = (chainVal, singleVal) => {
        if (Array.isArray(chainVal)) {
            chainVal.forEach(pushPath);
        } else if (chainVal != null && chainVal !== '') {
            pushPath(chainVal);
        }
        if (paths.length === 0 && singleVal != null && singleVal !== '') {
            pushPath(singleVal);
        }
    };
    if (body && typeof body === 'object') {
        addChainOrSingle(body.navigate_chain, body.navigate_to);
        if (paths.length === 0 && body.data && typeof body.data === 'object') {
            addChainOrSingle(body.data.navigate_chain, body.data.navigate_to);
        }
    }
    if (paths.length === 0) {
        const t = assistantRawMessage != null ? String(assistantRawMessage) : '';
        const rx = /"navigate_to"\s*:\s*"(\/[^"\\]*)"/gi;
        let m;
        while ((m = rx.exec(t)) !== null) {
            pushPath(m[1]);
        }
        const rx2 = /'navigate_to'\s*:\s*'(\/[^'\\]*)'/gi;
        while ((m = rx2.exec(t)) !== null) {
            pushPath(m[1]);
        }
    }
    return paths;
}

/** Hide raw {"navigate_to":"..."} blobs from the chat bubble. */
const AI_ASSISTANT_MAX_IMAGES = 5;
const AI_ASSISTANT_MAX_FILE_BYTES = 8 * 1024 * 1024;

/**
 * Action-mode “very quick” prompts (~25). Shuffled on load; 3–4 random picks shown (permission-filtered).
 * `perm`: single ability, null (always), or array (any one).
 */
const ACTION_PROMPT_POOL = [
    { langKey: 'ai_assistant_action_pool_01', perm: 'list-student' },
    { langKey: 'ai_assistant_action_pool_02', perm: 'list-student-fee' },
    { langKey: 'ai_assistant_action_pool_03', perm: 'list-student-attendance' },
    { langKey: 'ai_assistant_action_pool_04', perm: 'list-enquiry' },
    { langKey: 'ai_assistant_action_pool_05', perm: 'list-exam-schedule' },
    { langKey: 'ai_assistant_action_pool_06', perm: null },
    { langKey: 'ai_assistant_action_pool_07', perm: 'list-student-fee' },
    { langKey: 'ai_assistant_action_pool_08', perm: 'list-registration' },
    { langKey: 'ai_assistant_action_pool_09', perm: 'list-employee' },
    { langKey: 'ai_assistant_action_pool_10', perm: 'list-visitor-log' },
    { langKey: 'ai_assistant_action_pool_11', perm: 'list-call-log' },
    { langKey: 'ai_assistant_action_pool_12', perm: 'list-complaint' },
    { langKey: 'ai_assistant_action_pool_13', perm: 'list-book' },
    { langKey: 'ai_assistant_action_pool_14', perm: 'list-student' },
    { langKey: 'ai_assistant_action_pool_15', perm: 'list-student-attendance' },
    { langKey: 'ai_assistant_action_pool_16', perm: null },
    { langKey: 'ai_assistant_action_pool_17', perm: 'list-gate-pass' },
    { langKey: 'ai_assistant_action_pool_18', perm: 'list-postal-record' },
    { langKey: 'ai_assistant_action_pool_19', perm: 'access-exam-report' },
    { langKey: 'ai_assistant_action_pool_20', perm: 'list-exam-mark' },
    { langKey: 'ai_assistant_action_pool_21', perm: 'list-online-exam' },
    { langKey: 'ai_assistant_action_pool_22', perm: null },
    { langKey: 'ai_assistant_action_pool_23', perm: ['list-employee-attendance', 'mark-employee-attendance'] },
    { langKey: 'ai_assistant_action_pool_24', perm: null },
    { langKey: 'ai_assistant_action_pool_25', perm: 'list-student' },
];

function shuffleArrayInPlace(arr) {
    for (let i = arr.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
}

/**
 * If /js/lang is stale (Cache::rememberForever('lang.js')), trans() is empty for new keys — still show/send text.
 * Mirrors lang/en/configuration.php ai_assistant_action_pool_*.
 */
const ACTION_PROMPT_LABEL_FALLBACK = {
    ai_assistant_action_pool_01: 'How many students are enrolled right now, broken down by class or batch?',
    ai_assistant_action_pool_02: 'Which students have overdue or unpaid fee installments in the current session?',
    ai_assistant_action_pool_03: 'Summarize student attendance for the last few days — present, absent, and late trends.',
    ai_assistant_action_pool_04: 'Summarize recent reception enquiries — how many and what kinds of requests dominate?',
    ai_assistant_action_pool_05: 'What exams are coming up soon, and which classes or batches are involved?',
    ai_assistant_action_pool_06: 'What live Sipinor data can you query for me in Action mode with my current permissions?',
    ai_assistant_action_pool_07: 'How does fee collection look this month — collected versus still outstanding?',
    ai_assistant_action_pool_08: 'Give me a snapshot of registrations or admissions still in the pipeline.',
    ai_assistant_action_pool_09: 'How many active staff do we have by department and designation?',
    ai_assistant_action_pool_10: 'Summarize visitor log activity for this session — busy days and visitor patterns.',
    ai_assistant_action_pool_11: 'Summarize reception call logs — incoming vs outgoing and peak calling times.',
    ai_assistant_action_pool_12: 'How many complaints are still pending versus resolved, and by complaint type?',
    ai_assistant_action_pool_13: 'Library snapshot: titles, on-shelf copies, and how acquisitions look over time.',
    ai_assistant_action_pool_14: 'For a chosen class, what is the student headcount and quick demographic split you can show?',
    ai_assistant_action_pool_15: 'Attendance by class for the recent period — which sections need the most follow-up?',
    ai_assistant_action_pool_16: 'Open the student fee screen for me and explain what I can do on that page.',
    ai_assistant_action_pool_17: 'Summarize gate passes issued recently — student vs staff and common patterns.',
    ai_assistant_action_pool_18: 'Summarize postal records — dispatch versus receive activity in the date window I care about.',
    ai_assistant_action_pool_19: 'Exam results: what is published versus still unpublished across exams and classes?',
    ai_assistant_action_pool_20: 'How complete is exam marks entry compared to enrolled students for recent exams?',
    ai_assistant_action_pool_21: 'Online exams: published versus draft, attempts, and how many students have finished?',
    ai_assistant_action_pool_22: 'Suggest three everyday admin tasks I can do faster with Action mode today.',
    ai_assistant_action_pool_23: 'Summarize employee attendance for the current session window.',
    ai_assistant_action_pool_24: 'Open the main dashboard and recap the key institute numbers you can surface from my data.',
    ai_assistant_action_pool_25: 'New admissions or class movements this month — counts and how they spread across classes.',
};

function readFileAsDataUrl(file) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = () => reject(r.error || new Error('read'));
        r.readAsDataURL(file);
    });
}

function stripBareNavigateToJsonFromText(s) {
    let out = String(s || '');
    out = out.replace(/```(?:json)?\s*\{\s*"navigate_to"\s*:\s*"[^"]+"\s*\}\s*```/gi, '');
    out = out.replace(/\{\s*"navigate_to"\s*:\s*"[^"]+"\s*\}/gi, '');
    out = out.replace(/\{\s*'navigate_to'\s*:\s*'[^']+'\s*\}/gi, '');
    return out.replace(/\n{3,}/g, '\n\n').trim();
}

export default {
    data() {
        return {
            loading: false,
            capturingScreen: false,
            showHistory: false,
            isDark: typeof document !== 'undefined' && document.body && document.body.classList.contains('dark-mode'),
            /** Full-size data URL for attachment lightbox */
            attachmentPreviewSrc: null,
            /** Wider/taller panel (~2× default) */
            panelExpanded: false,
            /** Random subset of ACTION_PROMPT_POOL for this session / refresh points */
            actionPromptPickKeys: [],
        };
    },
    computed: {
        ...mapState({
            messages: (s) => s.aiAssistant.messages,
            mode: (s) => s.aiAssistant.mode,
            threadHistory: (s) => s.aiAssistant.threadHistory,
            pendingImages: (s) => s.aiAssistant.pendingImages || [],
            draft: (s) => s.aiAssistant.draft,
        }),
        panelOpen: {
            get() {
                return this.$store.state.aiAssistant.panelOpen;
            },
            set(v) {
                this.$store.commit('aiAssistantSetPanelOpen', v);
            },
        },
        showWidget() {
            return !!this.$store.getters.getConfig('ai_assistant_widget');
        },
        canSendContent() {
            const d = (this.draft || '').trim();
            return d.length > 0;
        },
        showActionSuggestedPrompts() {
            if (this.mode !== 'action' || this.loading) {
                return false;
            }
            return !this.messages.some((m) => m.role === 'user');
        },
        actionPromptLangKeys() {
            return this.actionPromptPickKeys;
        },
        actionPromptsSectionTitle() {
            const v = this.trans('configuration.ai_assistant_action_prompts_heading');
            return v != null && String(v).trim() !== '' ? String(v) : 'Very quick picks';
        },
    },
    watch: {
        messages: {
            handler() {
                this.$nextTick(() => this.scrollBottom());
            },
            deep: true,
        },
        panelOpen(v) {
            if (v) {
                this.syncDark();
                this.ensureWelcome();
                this.$nextTick(() => this.adjustTextareaHeight());
                document.addEventListener('click', this.onDocClick);
            } else {
                this.showHistory = false;
                this.closeAttachmentPreview();
                this.panelExpanded = false;
                document.removeEventListener('click', this.onDocClick);
            }
        },
        attachmentPreviewSrc(v) {
            if (v) {
                document.addEventListener('keydown', this.onAttachmentPreviewKeydown);
            } else {
                document.removeEventListener('keydown', this.onAttachmentPreviewKeydown);
            }
        },
        draft() {
            this.$nextTick(() => this.adjustTextareaHeight());
        },
        pendingImages: {
            handler(v) {
                if (v && v.length) {
                    this.$nextTick(() => this.scrollBottom());
                }
            },
            deep: true,
        },
        mode(m) {
            if (m === 'action' && !this.messages.some((msg) => msg.role === 'user')) {
                this.refreshActionPromptPick();
            }
        },
    },
    created() {
        this.$store.commit('aiAssistantEnsureShape');
    },
    mounted() {
        if (this.messages.length === 0) {
            this.ensureWelcome();
        }
        this.refreshActionPromptPick();
        this.$nextTick(() => this.adjustTextareaHeight());
    },
    beforeDestroy() {
        document.removeEventListener('click', this.onDocClick);
        document.removeEventListener('keydown', this.onAttachmentPreviewKeydown);
    },
    methods: {
        actionPromptRowAllowed(row) {
            if (!row || row.perm == null) {
                return true;
            }
            if (Array.isArray(row.perm)) {
                return this.$store.getters.hasAnyPermission(row.perm);
            }
            return this.$store.getters.hasPermission(row.perm);
        },
        refreshActionPromptPick() {
            const eligible = ACTION_PROMPT_POOL.filter((row) => this.actionPromptRowAllowed(row));
            const fallback = ACTION_PROMPT_POOL.filter((row) => !row.perm).map((row) => row.langKey);
            let pool = eligible.length ? [...eligible] : [];
            if (!pool.length) {
                this.actionPromptPickKeys = fallback.slice(0, 4);
                return;
            }
            shuffleArrayInPlace(pool);
            const n = 3 + Math.floor(Math.random() * 2);
            this.actionPromptPickKeys = pool.slice(0, Math.min(n, pool.length)).map((row) => row.langKey);
        },
        onDocClick(e) {
            if (!this.showHistory) {
                return;
            }
            const el = this.$el;
            if (el && !el.contains(e.target)) {
                this.showHistory = false;
            }
        },
        onAttachmentPreviewKeydown(e) {
            if (e.key === 'Escape') {
                this.closeAttachmentPreview();
            }
        },
        openAttachmentPreview(src) {
            if (!src || typeof src !== 'string' || src.indexOf('data:') !== 0) {
                return;
            }
            this.attachmentPreviewSrc = src;
        },
        actionPromptLabel(key) {
            const path = `configuration.${key}`;
            const v = this.trans(path);
            if (v != null && String(v).trim() !== '') {
                return String(v);
            }
            return ACTION_PROMPT_LABEL_FALLBACK[key] || key;
        },
        applyActionPrompt(text, options) {
            const opts = options && typeof options === 'object' ? options : {};
            const autoSend = !!opts.autoSend;
            const t = text != null ? String(text) : '';
            this.$store.commit('aiAssistantSetDraft', t);
            this.$nextTick(() => {
                const el = this.$refs.draftInput;
                if (el && el.tagName === 'TEXTAREA') {
                    el.focus();
                    const len = el.value.length;
                    el.setSelectionRange(len, len);
                    this.adjustTextareaHeight();
                }
                this.scrollBottom();
                if (autoSend && !this.loading && t.trim() !== '') {
                    /* send() starts with syncDraftFromInput(); wait until :value=draft reaches the textarea */
                    this.$nextTick(() => {
                        this.syncDraftFromInput();
                        this.$nextTick(() => {
                            if (!this.loading && ((this.draft || '').trim() || this.pendingImages.length)) {
                                this.send();
                            }
                        });
                    });
                }
            });
        },
        /** Fills composer and sends — quick picks should get an immediate assistant reply. */
        selectActionPrompt(key) {
            if (this.loading) {
                return;
            }
            const text = this.actionPromptLabel(key);
            if (!text || !String(text).trim()) {
                return;
            }
            this.applyActionPrompt(text, { autoSend: true });
        },
        closeAttachmentPreview() {
            this.attachmentPreviewSrc = null;
        },
        /** Full-resolution URL for sent message thumb (session only; stripped from localStorage). */
        userAttachmentFullSrc(m, idx) {
            if (!m || m.role !== 'user' || typeof idx !== 'number') {
                return '';
            }
            const arr = m.attachmentFullDataUrls;
            if (!Array.isArray(arr) || idx < 0 || idx >= arr.length) {
                return '';
            }
            const u = arr[idx];
            return typeof u === 'string' && u.indexOf('data:') === 0 ? u : '';
        },
        toggleHistory() {
            this.showHistory = !this.showHistory;
        },
        setMode(m) {
            this.$store.commit('aiAssistantSetMode', m);
        },
        closePanel() {
            this.panelOpen = false;
        },
        toggleFab() {
            this.panelOpen = !this.panelOpen;
            if (this.panelOpen) {
                this.syncDark();
            }
        },
        onDraftInput(e) {
            this.$store.commit('aiAssistantSetDraft', e.target.value);
            this.adjustTextareaHeight();
        },
        adjustTextareaHeight() {
            const el = this.$refs.draftInput;
            if (!el || el.tagName !== 'TEXTAREA') {
                return;
            }
            el.style.height = 'auto';
            const max = this.panelExpanded ? 264 : 132;
            const next = Math.min(max, Math.max(44, el.scrollHeight));
            el.style.height = `${next}px`;
        },
        togglePanelExpanded() {
            this.panelExpanded = !this.panelExpanded;
            this.$nextTick(() => {
                this.adjustTextareaHeight();
                this.scrollBottom();
            });
        },
        onDraftKeydown(e) {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!this.loading && (this.canSendContent || this.pendingImages.length)) {
                    this.send();
                }
            }
        },
        ensureWelcome() {
            if (this.messages.length === 0) {
                this.$store.commit('aiAssistantSetMessages', [
                    {
                        role: 'assistant',
                        content: this.trans('configuration.ai_assistant_welcome'),
                    },
                ]);
            }
        },
        formatAssistantHtml(raw) {
            if (raw == null || raw === '') {
                return '';
            }
            let s = String(raw);
            s = s.replace(/\\([*_`])/g, '$1');
            s = s.replace(/__([^_\n]+?)__/g, '**$1**');
            while (s.includes('****')) {
                s = s.replace(/\*\*\*\*/g, '**');
            }
            const esc = (t) => String(t)
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            const nlToBr = (t) => t.replace(/\r\n|\r|\n/g, '<br>');
            let out = '';
            let i = 0;
            while (i < s.length) {
                const start = s.indexOf('**', i);
                if (start === -1) {
                    out += nlToBr(esc(s.slice(i)));
                    break;
                }
                out += nlToBr(esc(s.slice(i, start)));
                const end = s.indexOf('**', start + 2);
                if (end === -1) {
                    out += nlToBr(esc(s.slice(start)));
                    break;
                }
                out += '<strong>'.concat(nlToBr(esc(s.slice(start + 2, end))), '</strong>');
                i = end + 2;
            }
            return out;
        },
        syncDark() {
            this.isDark = document.body && document.body.classList.contains('dark-mode');
        },
        scrollBottom() {
            const el = this.$refs.scrollArea;
            if (el) {
                el.scrollTop = el.scrollHeight;
            }
        },
        syncDraftFromInput() {
            const el = this.$refs.draftInput;
            if (el && typeof el.value === 'string') {
                this.$store.commit('aiAssistantSetDraft', el.value);
            }
        },
        async navigateSipinorPathChain(paths, options) {
            const arr = (paths || [])
                .map((p) => (p != null ? String(p).trim() : ''))
                .filter((p) => p.charAt(0) === '/');
            if (!arr.length) {
                return;
            }
            const router = this.$router || (this.$root && this.$root.$router);
            const forceLast = options && options.forceReloadLast === true;
            const lastIdx = arr.length - 1;
            const stepDelayMs = 180;
            for (let i = 0; i < arr.length; i += 1) {
                const p = arr[i];
                if (i === lastIdx) {
                    this.navigateToSipinorPath(p, { forceReload: forceLast });
                    return;
                }
                if (router) {
                    try {
                        await router.push(p);
                    } catch (err) {
                        if (err && err.name === 'NavigationDuplicated') {
                            /* already there */
                        } else {
                            this.navigateToSipinorPath(p, { forceReload: false });
                            return;
                        }
                    }
                    await new Promise((r) => setTimeout(r, stepDelayMs));
                } else {
                    this.navigateToSipinorPath(p, { forceReload: false });
                    return;
                }
            }
        },
        navigateToSipinorPath(navPath, options) {
            const path = navPath != null ? String(navPath).trim() : '';
            if (!path || path.charAt(0) !== '/') {
                return;
            }
            if (typeof window === 'undefined' || !window.location) {
                return;
            }
            const forceReload = options && options.forceReload === true;
            const router = this.$router || (this.$root && this.$root.$router);
            let href = path;
            if (router) {
                try {
                    const resolved = router.resolve(path);
                    if (resolved && resolved.href) {
                        href = resolved.href;
                    }
                } catch (e) {
                    /* use path */
                }
            }
            let absolute;
            if (href.indexOf('http://') === 0 || href.indexOf('https://') === 0) {
                absolute = href;
            } else {
                absolute = window.location.origin + (href.charAt(0) === '/' ? href : `/${href}`);
            }
            let targetPathname = path;
            try {
                targetPathname = new URL(absolute).pathname;
            } catch (e2) {
                /* keep path */
            }
            if (window.location.pathname === targetPathname) {
                if (forceReload) {
                    window.location.reload();
                }
                return;
            }
            window.location.assign(absolute);
        },
        snapshotActiveThreadToHistory() {
            const sid = this.$store.state.aiAssistant.sessionHistoryId;
            if (!sid) {
                return;
            }
            const msgs = this.messages.filter((m) => m.role === 'user' || m.role === 'assistant');
            if (msgs.length <= 1) {
                return;
            }
            const hasUser = msgs.some((m) => m.role === 'user');
            if (!hasUser) {
                return;
            }
            const firstUser = msgs.find((m) => m.role === 'user');
            const title = (firstUser && firstUser.content ? String(firstUser.content) : 'Chat').slice(0, 52);
            const entry = {
                id: sid,
                title: title || 'Chat',
                messages: msgs.map((m) => ({
                    role: m.role,
                    content: m.content,
                    screenCapture: !!m.screenCapture,
                    screenCapturePreview: m.screenCapturePreview || null,
                    attachmentPreviews: Array.isArray(m.attachmentPreviews) ? m.attachmentPreviews.slice() : null,
                    attachmentFullDataUrls: Array.isArray(m.attachmentFullDataUrls) ? m.attachmentFullDataUrls.slice() : null,
                })),
                mode: this.mode,
                savedAt: new Date().toISOString(),
            };
            const history = [...this.threadHistory];
            const idx = history.findIndex((h) => h.id === sid);
            if (idx >= 0) {
                history.splice(idx, 1);
            }
            history.unshift(entry);
            this.$store.commit('aiAssistantSetThreadHistory', history.slice(0, 20));
        },
        removePendingImage(id) {
            this.$store.commit('aiAssistantRemovePendingImage', id);
        },
        openImagePicker() {
            const el = this.$refs.imageFileInput;
            if (el) {
                el.value = '';
                el.click();
            }
        },
        async onImageFilesSelected(e) {
            const input = e && e.target;
            const files = input && input.files ? Array.from(input.files) : [];
            if (input) {
                input.value = '';
            }
            if (!files.length) {
                return;
            }
            const allowed = { 'image/jpeg': true, 'image/png': true, 'image/webp': true };
            let room = AI_ASSISTANT_MAX_IMAGES - this.pendingImages.length;
            if (room <= 0) {
                if (typeof toastr !== 'undefined') {
                    toastr.warning(this.trans('configuration.ai_assistant_max_images_reached'));
                }
                return;
            }
            for (let i = 0; i < files.length; i += 1) {
                if (room <= 0) {
                    break;
                }
                const file = files[i];
                if (!allowed[file.type]) {
                    if (typeof toastr !== 'undefined') {
                        toastr.error(this.trans('configuration.ai_assistant_invalid_image_type'));
                    }
                    continue;
                }
                if (file.size > AI_ASSISTANT_MAX_FILE_BYTES) {
                    if (typeof toastr !== 'undefined') {
                        toastr.error(this.trans('configuration.ai_assistant_image_too_large'));
                    }
                    continue;
                }
                try {
                    const dataUrl = await readFileAsDataUrl(file);
                    if (typeof dataUrl !== 'string' || dataUrl.indexOf('data:') !== 0) {
                        continue;
                    }
                    const comma = dataUrl.indexOf(',');
                    const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
                    if (!base64) {
                        continue;
                    }
                    this.$store.commit('aiAssistantAddPendingImage', {
                        id: `up-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
                        base64,
                        mime: file.type,
                        previewDataUrl: dataUrl,
                    });
                    room -= 1;
                } catch (err) {
                    if (typeof toastr !== 'undefined') {
                        toastr.error(this.trans('configuration.ai_assistant_invalid_image_type'));
                    }
                }
            }
            this.$nextTick(() => this.scrollBottom());
        },
        /** Small thumbnails for sent user messages (legacy single preview supported). */
        userAttachmentThumbs(m) {
            if (!m || m.role !== 'user') {
                return [];
            }
            if (Array.isArray(m.attachmentPreviews) && m.attachmentPreviews.length) {
                return m.attachmentPreviews.filter((u) => typeof u === 'string' && u.indexOf('data:') === 0);
            }
            const p = m.screenCapturePreview;
            if (typeof p === 'string' && p.indexOf('data:') === 0) {
                return [p];
            }
            return [];
        },
        /** Hide auto-filled screenshot-only default in the bubble; typed text still shows. */
        userMessageDisplayText(m) {
            if (!m || m.role !== 'user') {
                return '';
            }
            const raw = m.content != null ? String(m.content).trim() : '';
            if (raw === '') {
                return '';
            }
            const def = String(this.trans('configuration.ai_assistant_message_default_with_screenshot') || '').trim();
            if (m.screenCapture && def !== '' && raw === def) {
                return '';
            }
            return m.content != null ? String(m.content) : '';
        },
        buildHistoryScreenshotPreview(dataUrl, maxW = 280) {
            if (!dataUrl || typeof dataUrl !== 'string') {
                return Promise.resolve(null);
            }
            return new Promise((resolve) => {
                const fallback = () => resolve(dataUrl);
                const timer = setTimeout(fallback, 6000);
                const img = new Image();
                img.onload = () => {
                    clearTimeout(timer);
                    try {
                        let w = img.naturalWidth || img.width;
                        let h = img.naturalHeight || img.height;
                        if (w < 1 || h < 1) {
                            resolve(dataUrl);
                            return;
                        }
                        if (w > maxW) {
                            h = Math.round((h * maxW) / w);
                            w = maxW;
                        }
                        const c = document.createElement('canvas');
                        c.width = w;
                        c.height = h;
                        const ctx = c.getContext('2d');
                        if (!ctx) {
                            resolve(dataUrl);
                            return;
                        }
                        ctx.drawImage(img, 0, 0, w, h);
                        resolve(c.toDataURL('image/jpeg', 0.72));
                    } catch (e) {
                        resolve(dataUrl);
                    }
                };
                img.onerror = () => {
                    clearTimeout(timer);
                    resolve(dataUrl);
                };
                img.src = dataUrl;
            });
        },
        archiveCurrentThread() {
            const msgs = this.messages.filter((m) => m.role === 'user' || m.role === 'assistant');
            if (msgs.length <= 1) {
                return;
            }
            const hasUser = msgs.some((m) => m.role === 'user');
            if (!hasUser) {
                return;
            }
            const firstUser = msgs.find((m) => m.role === 'user');
            const title = (firstUser && firstUser.content ? String(firstUser.content) : 'Chat').slice(0, 52);
            const entry = {
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                title: title || 'Chat',
                messages: msgs.map((m) => ({
                    role: m.role,
                    content: m.content,
                    screenCapture: !!m.screenCapture,
                    screenCapturePreview: m.screenCapturePreview || null,
                    attachmentPreviews: Array.isArray(m.attachmentPreviews) ? m.attachmentPreviews.slice() : null,
                    attachmentFullDataUrls: Array.isArray(m.attachmentFullDataUrls) ? m.attachmentFullDataUrls.slice() : null,
                })),
                mode: this.mode,
                savedAt: new Date().toISOString(),
            };
            const next = [entry, ...this.threadHistory].slice(0, 20);
            this.$store.commit('aiAssistantSetThreadHistory', next);
        },
        startNewChat() {
            this.loading = false;
            this.closeAttachmentPreview();
            const sid = this.$store.state.aiAssistant.sessionHistoryId;
            if (sid) {
                const history = this.threadHistory.filter((h) => h.id !== sid);
                this.$store.commit('aiAssistantSetThreadHistory', history);
            }
            this.archiveCurrentThread();
            this.$store.commit('aiAssistantSetSessionHistoryId', null);
            this.$store.commit('aiAssistantClearPendingImages');
            this.$store.commit('aiAssistantSetDraft', '');
            this.$store.commit('aiAssistantSetMessages', [
                {
                    role: 'assistant',
                    content: this.trans('configuration.ai_assistant_welcome'),
                },
            ]);
            this.showHistory = false;
            if (this.mode === 'action') {
                this.refreshActionPromptPick();
            }
            this.$nextTick(() => {
                this.adjustTextareaHeight();
            });
        },
        loadThread(h) {
            if (!h || !Array.isArray(h.messages)) {
                return;
            }
            this.archiveCurrentThread();
            this.$store.commit(
                'aiAssistantSetSessionHistoryId',
                h.id || `thread-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            );
            this.$store.commit('aiAssistantClearPendingImages');
            this.$store.commit('aiAssistantSetDraft', '');
            this.$store.commit('aiAssistantSetMessages', JSON.parse(JSON.stringify(h.messages)));
            const loadedMode = h.mode || 'suggestion';
            this.$store.commit('aiAssistantSetMode', loadedMode);
            if (loadedMode === 'action' && !h.messages.some((m) => m.role === 'user')) {
                this.refreshActionPromptPick();
            }
            this.showHistory = false;
            this.$nextTick(() => {
                this.scrollBottom();
                this.adjustTextareaHeight();
            });
        },
        async captureScreen() {
            if (this.capturingScreen || this.loading) {
                return;
            }
            if (this.pendingImages.length >= AI_ASSISTANT_MAX_IMAGES) {
                if (typeof toastr !== 'undefined') {
                    toastr.warning(this.trans('configuration.ai_assistant_max_images_reached'));
                }
                return;
            }
            this.capturingScreen = true;
            try {
                const root = document.querySelector('.nk-app-root')
                    || document.getElementById('app')
                    || document.body;
                const canvas = await html2canvas(root, {
                    useCORS: true,
                    allowTaint: true,
                    logging: false,
                    scale: Math.min(1.15, window.devicePixelRatio || 1),
                    onclone: (doc) => {
                        doc.querySelectorAll('.sipinor-ai-widget').forEach((el) => {
                            el.style.visibility = 'hidden';
                        });
                    },
                });
                let out = canvas;
                const maxW = 1280;
                if (canvas.width > maxW) {
                    const c2 = document.createElement('canvas');
                    c2.width = maxW;
                    c2.height = Math.round(canvas.height * (maxW / canvas.width));
                    const ctx = c2.getContext('2d');
                    ctx.drawImage(canvas, 0, 0, c2.width, c2.height);
                    out = c2;
                }
                const previewDataUrl = out.toDataURL('image/jpeg', 0.82);
                const base64 = previewDataUrl.split(',')[1];
                this.$store.commit('aiAssistantAddPendingImage', {
                    id: `cap-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
                    base64,
                    mime: 'image/jpeg',
                    previewDataUrl,
                });
                this.$nextTick(() => this.scrollBottom());
            } catch (err) {
                if (typeof toastr !== 'undefined') {
                    toastr.error(this.trans('configuration.ai_assistant_capture_failed'));
                }
            } finally {
                this.capturingScreen = false;
            }
        },
        pushAssistantImmediate(msg) {
            this.$store.commit('aiAssistantPushMessage', {
                role: 'assistant',
                content: msg,
            });
            this.snapshotActiveThreadToHistory();
        },
        async send() {
            this.syncDraftFromInput();
            const draftTrim = (this.draft || '').trim();
            const pendingList = this.pendingImages || [];
            const hasImages = pendingList.length > 0;
            if (!draftTrim && !hasImages) {
                return;
            }
            if (this.loading) {
                return;
            }
            if (!this.$store.state.aiAssistant.sessionHistoryId) {
                this.$store.commit(
                    'aiAssistantSetSessionHistoryId',
                    `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
                );
            }
            const text = draftTrim
                || this.trans('configuration.ai_assistant_message_default_with_screenshot');
            const stripMaxW = 56;
            const attachmentPreviews = [];
            if (hasImages) {
                for (let i = 0; i < pendingList.length; i += 1) {
                    const p = pendingList[i];
                    const captureDataUrl = p.previewDataUrl
                        || (p.base64 && p.mime ? `data:${p.mime};base64,${p.base64}` : null);
                    if (!captureDataUrl) {
                        continue;
                    }
                    const shrunk = await this.buildHistoryScreenshotPreview(captureDataUrl, stripMaxW);
                    const prev = (shrunk && String(shrunk).indexOf('data:') === 0) ? shrunk : captureDataUrl;
                    attachmentPreviews.push(prev);
                }
            }
            this.$store.commit('aiAssistantSetDraft', '');
            this.$nextTick(() => this.adjustTextareaHeight());
            const attachmentFullDataUrls = hasImages
                ? pendingList
                    .map((p) => p.previewDataUrl
                        || (p.base64 && p.mime ? `data:${p.mime};base64,${p.base64}` : ''))
                    .filter((u) => typeof u === 'string' && u.indexOf('data:') === 0)
                : null;
            this.$store.commit('aiAssistantPushMessage', {
                role: 'user',
                content: text,
                screenCapture: hasImages,
                attachmentPreviews: hasImages && attachmentPreviews.length ? attachmentPreviews : null,
                attachmentFullDataUrls: hasImages && attachmentFullDataUrls && attachmentFullDataUrls.length
                    ? attachmentFullDataUrls
                    : null,
                screenCapturePreview: hasImages && attachmentPreviews.length ? attachmentPreviews[0] : null,
            });
            let payloadMessages = this.$store.state.aiAssistant.messages
                .filter((m) => m.role === 'user' || m.role === 'assistant')
                .filter((m) => {
                    const c = m.content != null ? String(m.content) : '';
                    return c.trim() !== '';
                })
                .map((m) => ({
                    role: m.role,
                    content: m.content != null ? String(m.content) : '',
                }));
            if (payloadMessages.length === 0) {
                payloadMessages = [{ role: 'user', content: text }];
            }
            const screenshots = hasImages
                ? pendingList.map((p) => ({
                    image_base64: p.base64,
                    mime: p.mime || 'image/jpeg',
                }))
                : null;
            if (hasImages) {
                this.$store.commit('aiAssistantClearPendingImages');
            }
            this.loading = true;
            axios
                .post('/api/ai-assistant/chat', {
                    mode: this.mode,
                    messages: payloadMessages.slice(-20),
                    ...(screenshots && screenshots.length ? { screenshots } : {}),
                })
                .then((body) => {
                    let payload = body;
                    if (body && typeof body === 'object' && body.data && typeof body.data === 'object'
                        && !Array.isArray(body.data)) {
                        payload = body.data;
                    }
                    const raw = payload && typeof payload === 'object' && payload.message != null
                        ? String(payload.message)
                        : '';
                    const navPaths = extractNavigatePathsFromApi(
                        payload && typeof payload === 'object' ? payload : {},
                        raw,
                    );
                    let msg = stripBareNavigateToJsonFromText(raw).trim();
                    if (!msg && navPaths.length) {
                        const opened = this.trans('configuration.ai_assistant_opened_screen');
                        msg = opened != null && String(opened).trim() !== '' ? String(opened) : 'Opening that screen for you.';
                    }
                    if (!msg) {
                        const errTxt = this.trans('configuration.ai_assistant_openai_error');
                        msg = errTxt != null && String(errTxt).trim() !== '' ? String(errTxt) : 'The AI service returned an error.';
                    }
                    if (!String(msg).trim()) {
                        msg = this.trans('configuration.ai_assistant_empty_reply');
                        if (!String(msg).trim()) {
                            msg = 'No reply text was returned. Try again or check AI configuration.';
                        }
                    }
                    this.$store.commit('aiAssistantPushMessage', {
                        role: 'assistant',
                        content: msg,
                    });
                    const runNav = () => {
                        if (!payload || typeof payload !== 'object') {
                            return;
                        }
                        if (navPaths.length === 1) {
                            const forceReload = payload.force_reload === true;
                            this.$nextTick(() => this.navigateToSipinorPath(navPaths[0], { forceReload }));
                        } else if (navPaths.length > 1) {
                            const forceReloadLast = payload.force_reload === true;
                            this.$nextTick(() => {
                                this.navigateSipinorPathChain(navPaths, { forceReloadLast });
                            });
                        }
                    };
                    this.$nextTick(() => {
                        this.scrollBottom();
                        this.snapshotActiveThreadToHistory();
                        runNav();
                    });
                })
                .catch((err) => {
                    const d = err.response && err.response.data;
                    let txt = this.trans('configuration.ai_assistant_openai_error');
                    if (txt == null || String(txt).trim() === '') {
                        txt = 'The AI service returned an error. Check the API key and model name.';
                    }
                    if (d && d.errors && typeof d.errors === 'object') {
                        const parts = [];
                        Object.keys(d.errors).forEach((k) => {
                            const v = d.errors[k];
                            if (Array.isArray(v)) {
                                v.forEach((x) => {
                                    if (typeof x === 'string' && x.trim() !== '') {
                                        parts.push(x.trim());
                                    }
                                });
                            } else if (typeof v === 'string' && v.trim() !== '') {
                                parts.push(v.trim());
                            }
                        });
                        if (parts.length) {
                            txt = parts.join(' ');
                        }
                    } else if (d && d.errors && d.errors.message) {
                        const first = Array.isArray(d.errors.message) ? d.errors.message[0] : d.errors.message;
                        if (typeof first === 'string' && first.trim() !== '') {
                            txt = first;
                        }
                    } else if (d && typeof d.message === 'string' && d.message.trim() !== '') {
                        txt = d.message;
                    }
                    this.pushAssistantImmediate(txt);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
    },
};
</script>

<style scoped>
.sipinor-ai-widget {
    --sipinor-ai-accent: #6366f1;
    --sipinor-ai-accent2: #8b5cf6;
    --sipinor-ai-user: linear-gradient(135deg, #4f46e5 0%, #6366f1 55%, #7c3aed 100%);
    --sipinor-ai-surface: #ffffff;
    --sipinor-ai-muted: #64748b;
    --sipinor-ai-border: rgba(15, 23, 42, 0.08);
    --sipinor-ai-assistant-bg: #f8fafc;
    position: fixed;
    right: 22px;
    bottom: 22px;
    /* Stay above modals, drawers, and dense dashboard chrome so the chat control is always reachable */
    z-index: 10060;
    isolation: isolate;
    font-size: 13px;
    font-family: ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.45;
}

.sipinor-ai-panel--dark {
    --sipinor-ai-surface: #0f172a;
    --sipinor-ai-muted: #94a3b8;
    --sipinor-ai-border: rgba(248, 250, 252, 0.1);
    --sipinor-ai-assistant-bg: #1e293b;
}

.sipinor-ai-fab {
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    cursor: pointer;
    background: linear-gradient(145deg, var(--sipinor-ai-accent) 0%, var(--sipinor-ai-accent2) 100%);
    color: #fff;
    box-shadow:
        0 4px 14px rgba(99, 102, 241, 0.45),
        0 0 0 1px rgba(255, 255, 255, 0.12) inset;
    transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.22s ease;
}
.sipinor-ai-fab:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 22px rgba(99, 102, 241, 0.5);
}
.sipinor-ai-fab--action:not(.sipinor-ai-fab--open) {
    background: linear-gradient(145deg, #0f766e 0%, #14b8a6 52%, #10b981 100%);
    box-shadow:
        0 4px 14px rgba(13, 148, 136, 0.48),
        0 0 0 1px rgba(255, 255, 255, 0.12) inset;
}
.sipinor-ai-fab--action:not(.sipinor-ai-fab--open):hover {
    box-shadow: 0 6px 22px rgba(13, 148, 136, 0.52);
}
.sipinor-ai-fab--open {
    background: #334155;
    box-shadow: 0 4px 16px rgba(15, 23, 42, 0.35);
}
.sipinor-ai-fab .icon {
    font-size: 1.45rem;
}

.sipinor-ai-panel {
    position: absolute;
    right: 0;
    bottom: 72px;
    z-index: 1;
    width: min(400px, calc(100vw - 32px));
    max-height: min(580px, calc(100vh - 96px));
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-radius: 16px;
    background: var(--sipinor-ai-surface);
    color: #0f172a;
    border: 1px solid var(--sipinor-ai-border);
    box-shadow:
        0 24px 48px -12px rgba(15, 23, 42, 0.22),
        0 0 0 1px rgba(255, 255, 255, 0.06) inset;
    transition:
        border-color 0.22s ease,
        box-shadow 0.22s ease,
        width 0.28s ease,
        max-height 0.28s ease;
}

.sipinor-ai-panel--expanded {
    width: min(800px, calc(100vw - 32px));
    max-height: min(1160px, calc(100vh - 96px));
}
.sipinor-ai-panel--expanded .sipinor-ai-messages {
    min-height: 440px;
    max-height: min(640px, 84vh);
}
.sipinor-ai-panel--expanded .sipinor-ai-textarea {
    max-height: 264px;
}
.sipinor-ai-panel--expanded .sipinor-ai-history-pop {
    max-height: 440px;
}

/* Mode chrome: Suggestion = indigo/violet · Action = teal/emerald */
.sipinor-ai-panel--mode-suggestion::before,
.sipinor-ai-panel--mode-action::before {
    content: '';
    display: block;
    height: 3px;
    flex-shrink: 0;
}
.sipinor-ai-panel--mode-suggestion::before {
    background: linear-gradient(90deg, #6366f1, #a855f7);
}
.sipinor-ai-panel--mode-action::before {
    background: linear-gradient(90deg, #0f766e, #10b981);
}
.sipinor-ai-panel--mode-suggestion {
    border-color: rgba(99, 102, 241, 0.24);
}
.sipinor-ai-panel--mode-action {
    border-color: rgba(13, 148, 136, 0.35);
    box-shadow:
        0 24px 48px -12px rgba(15, 23, 42, 0.22),
        0 0 0 1px rgba(13, 148, 136, 0.1) inset;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark {
    border-color: rgba(45, 212, 191, 0.3);
    box-shadow:
        0 24px 48px -12px rgba(0, 0, 0, 0.35),
        0 0 0 1px rgba(45, 212, 191, 0.08) inset;
}

.sipinor-ai-panel--dark {
    color: #f1f5f9;
}

.sipinor-ai-panel__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    padding: 14px 14px 12px;
    background: linear-gradient(180deg, rgba(99, 102, 241, 0.07) 0%, transparent 100%);
    border-bottom: 1px solid var(--sipinor-ai-border);
}
.sipinor-ai-panel--dark .sipinor-ai-panel__head {
    background: linear-gradient(180deg, rgba(99, 102, 241, 0.12) 0%, transparent 100%);
}
.sipinor-ai-panel--mode-action .sipinor-ai-panel__head {
    background: linear-gradient(180deg, rgba(13, 148, 136, 0.11) 0%, transparent 100%);
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-panel__head {
    background: linear-gradient(180deg, rgba(45, 212, 191, 0.14) 0%, transparent 100%);
}

.sipinor-ai-panel__titles {
    min-width: 0;
}
.sipinor-ai-panel__title-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 8px;
}
.sipinor-ai-panel__mode-chip {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    padding: 3px 7px;
    border-radius: 6px;
    line-height: 1.25;
    flex-shrink: 0;
}
.sipinor-ai-panel__mode-chip--suggestion {
    background: rgba(99, 102, 241, 0.16);
    color: #4338ca;
}
.sipinor-ai-panel--dark .sipinor-ai-panel__mode-chip--suggestion {
    background: rgba(196, 181, 253, 0.22);
    color: #e9d5ff;
}
.sipinor-ai-panel__mode-chip--action {
    background: rgba(13, 148, 136, 0.2);
    color: #0f766e;
}
.sipinor-ai-panel--dark .sipinor-ai-panel__mode-chip--action {
    background: rgba(45, 212, 191, 0.18);
    color: #99f6e4;
}

.sipinor-ai-panel__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
}
.sipinor-ai-panel__avatar {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: linear-gradient(135deg, var(--sipinor-ai-accent), var(--sipinor-ai-accent2));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
}
.sipinor-ai-panel--mode-action .sipinor-ai-panel__avatar {
    background: linear-gradient(135deg, #0f766e, #059669);
    box-shadow: 0 2px 10px rgba(13, 148, 136, 0.4);
}
.sipinor-ai-panel__title {
    font-weight: 650;
    font-size: 15px;
    letter-spacing: -0.02em;
}
.sipinor-ai-panel__subtitle {
    font-size: 11px;
    color: var(--sipinor-ai-muted);
    margin-top: 2px;
}
.sipinor-ai-panel__head-actions {
    display: flex;
    align-items: flex-start;
    gap: 2px;
    flex-shrink: 0;
}
.sipinor-ai-panel__icon-btn {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--sipinor-ai-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
}
.sipinor-ai-panel__icon-btn:hover {
    background: rgba(99, 102, 241, 0.1);
    color: var(--sipinor-ai-accent);
}
.sipinor-ai-panel--mode-action .sipinor-ai-panel__icon-btn:hover {
    background: rgba(13, 148, 136, 0.14);
    color: #0f766e;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-panel__icon-btn:hover {
    color: #5eead4;
}
.sipinor-ai-panel__icon-btn .icon {
    font-size: 1.15rem;
}

.sipinor-ai-panel__toolbar {
    position: relative;
    flex-shrink: 0;
    padding: 10px 12px 0;
}

.sipinor-ai-segmented {
    display: flex;
    width: 100%;
    padding: 3px;
    border-radius: 11px;
    background: rgba(15, 23, 42, 0.06);
    gap: 2px;
}
.sipinor-ai-panel--dark .sipinor-ai-segmented {
    background: rgba(248, 250, 252, 0.08);
}
.sipinor-ai-segmented__btn {
    flex: 1 1 auto;
    min-width: 0;
    border: none;
    border-radius: 8px;
    padding: 7px 8px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    background: transparent;
    color: var(--sipinor-ai-muted);
    transition: background 0.15s, color 0.15s, box-shadow 0.15s;
}
.sipinor-ai-segmented__btn--icon {
    flex: 0 0 38px;
}
.sipinor-ai-segmented__btn.is-active {
    background: var(--sipinor-ai-surface);
    color: var(--sipinor-ai-accent);
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}
.sipinor-ai-panel--dark .sipinor-ai-segmented__btn.is-active {
    background: #334155;
    color: #c4b5fd;
    box-shadow: none;
}
.sipinor-ai-panel--mode-action .sipinor-ai-segmented__btn.is-active {
    color: #0f766e;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-segmented__btn.is-active {
    color: #5eead4;
}

.sipinor-ai-history-pop {
    position: absolute;
    left: 12px;
    right: 12px;
    top: 100%;
    margin-top: 6px;
    z-index: 5;
    max-height: 220px;
    overflow-y: auto;
    padding: 10px;
    border-radius: 12px;
    background: var(--sipinor-ai-surface);
    border: 1px solid var(--sipinor-ai-border);
    box-shadow: 0 12px 32px rgba(15, 23, 42, 0.15);
    text-align: left;
}
.sipinor-ai-history-pop__new {
    width: 100%;
    border: 1px solid var(--sipinor-ai-accent);
    color: var(--sipinor-ai-accent);
    background: rgba(99, 102, 241, 0.06);
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    margin-bottom: 8px;
}
.sipinor-ai-panel--mode-action .sipinor-ai-history-pop__new {
    border-color: #0d9488;
    color: #0f766e;
    background: rgba(13, 148, 136, 0.08);
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-history-pop__new {
    border-color: #2dd4bf;
    color: #5eead4;
    background: rgba(45, 212, 191, 0.1);
}
.sipinor-ai-history-pop__empty,
.sipinor-ai-history-pop__note {
    font-size: 11px;
    color: var(--sipinor-ai-muted);
    margin-bottom: 6px;
}
.sipinor-ai-history-item {
    display: block;
    width: 100%;
    text-align: left;
    border: none;
    background: rgba(15, 23, 42, 0.04);
    border-radius: 8px;
    padding: 8px 10px;
    margin-bottom: 4px;
    font-size: 12px;
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: inherit;
}
.sipinor-ai-panel--dark .sipinor-ai-history-item {
    background: rgba(248, 250, 252, 0.06);
}

.sipinor-ai-panel__body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 10px 12px 14px;
    flex: 1;
}
/* Collapsed panel: header + mode bar + messages min-height + quick picks + composer exceeded
   max-height and was clipped by .sipinor-ai-panel overflow — scroll the body so composer stays reachable. */
.sipinor-ai-panel:not(.sipinor-ai-panel--expanded) .sipinor-ai-panel__body {
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-width: thin;
}
.sipinor-ai-panel:not(.sipinor-ai-panel--expanded) .sipinor-ai-panel__body::-webkit-scrollbar {
    width: 6px;
}
.sipinor-ai-panel:not(.sipinor-ai-panel--expanded) .sipinor-ai-panel__body::-webkit-scrollbar-thumb {
    background: rgba(15, 23, 42, 0.15);
    border-radius: 6px;
}
.sipinor-ai-panel--dark:not(.sipinor-ai-panel--expanded) .sipinor-ai-panel__body::-webkit-scrollbar-thumb {
    background: rgba(248, 250, 252, 0.2);
}

.sipinor-ai-action-prompts {
    flex-shrink: 0;
    padding: 2px 0 10px;
    margin-top: -2px;
}
.sipinor-ai-action-prompts__heading {
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 650;
    color: var(--sipinor-ai-muted);
    letter-spacing: 0.02em;
}
.sipinor-ai-panel--mode-action .sipinor-ai-action-prompts__heading {
    color: #0f766e;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-action-prompts__heading {
    color: #5eead4;
}
.sipinor-ai-action-prompts__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.sipinor-ai-action-prompts__chip {
    text-align: left;
    border: 1px solid rgba(13, 148, 136, 0.32);
    background: rgba(13, 148, 136, 0.07);
    color: inherit;
    border-radius: 11px;
    padding: 8px 12px;
    font-size: 11px;
    line-height: 1.4;
    cursor: pointer;
    max-width: 100%;
    transition: background 0.15s, border-color 0.15s, transform 0.12s;
}
.sipinor-ai-action-prompts__chip:hover {
    background: rgba(13, 148, 136, 0.14);
    border-color: rgba(13, 148, 136, 0.5);
    transform: translateY(-1px);
}
.sipinor-ai-panel--dark .sipinor-ai-action-prompts__chip {
    border-color: rgba(45, 212, 191, 0.35);
    background: rgba(45, 212, 191, 0.09);
}
.sipinor-ai-panel--dark .sipinor-ai-action-prompts__chip:hover {
    background: rgba(45, 212, 191, 0.16);
    border-color: rgba(45, 212, 191, 0.5);
}

.sipinor-ai-messages {
    flex: 1;
    min-height: 220px;
    max-height: min(320px, 42vh);
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 4px 2px 8px;
    scrollbar-width: thin;
}
.sipinor-ai-panel:not(.sipinor-ai-panel--expanded) .sipinor-ai-messages {
    flex: 0 1 auto;
    min-height: 96px;
    max-height: min(260px, 40vh);
}
.sipinor-ai-messages::-webkit-scrollbar {
    width: 6px;
}
.sipinor-ai-messages::-webkit-scrollbar-thumb {
    background: rgba(15, 23, 42, 0.15);
    border-radius: 6px;
}

.sipinor-ai-msg--user {
    text-align: right;
}
.sipinor-ai-msg__assistant-wrap {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    max-width: 100%;
}
.sipinor-ai-msg__avatar-mini {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: rgba(99, 102, 241, 0.12);
    color: var(--sipinor-ai-accent);
    display: flex;
    align-items: center;
    justify-content: center;
}
.sipinor-ai-panel--dark .sipinor-ai-msg__avatar-mini {
    background: rgba(196, 181, 253, 0.15);
    color: #c4b5fd;
}
.sipinor-ai-panel--mode-action .sipinor-ai-msg__avatar-mini {
    background: rgba(13, 148, 136, 0.16);
    color: #0f766e;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-msg__avatar-mini {
    background: rgba(45, 212, 191, 0.14);
    color: #5eead4;
}

.sipinor-ai-msg__bubble {
    max-width: 100%;
    padding: 10px 14px;
    border-radius: 14px;
    word-break: break-word;
    text-align: left;
}
.sipinor-ai-msg__bubble--user {
    display: inline-block;
    max-width: min(100%, 280px);
    background: var(--sipinor-ai-user);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 2px 10px rgba(79, 70, 229, 0.25);
    vertical-align: top;
}
.sipinor-ai-msg__bubble--user-with-media {
    max-width: min(100%, 260px);
    padding: 8px 10px;
}
.sipinor-ai-msg__text {
    white-space: pre-wrap;
}
.sipinor-ai-msg__bubble--assistant {
    flex: 1;
    min-width: 0;
    background: var(--sipinor-ai-assistant-bg);
    color: #0f172a;
    border: 1px solid var(--sipinor-ai-border);
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.sipinor-ai-panel--dark .sipinor-ai-msg__bubble--assistant {
    color: #f1f5f9;
}

.sipinor-ai-typing {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    min-height: 22px;
    padding: 12px 16px !important;
}
.sipinor-ai-typing__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--sipinor-ai-accent);
    opacity: 0.35;
    animation: sipinor-ai-bounce 1.15s ease-in-out infinite;
}
.sipinor-ai-typing__dot:nth-child(2) {
    animation-delay: 0.18s;
}
.sipinor-ai-typing__dot:nth-child(3) {
    animation-delay: 0.36s;
}
.sipinor-ai-panel--mode-action .sipinor-ai-typing__dot {
    background: #0d9488;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-typing__dot {
    background: #2dd4bf;
}
@keyframes sipinor-ai-bounce {
    0%,
    60%,
    100% {
        opacity: 0.3;
        transform: translateY(0);
    }
    30% {
        opacity: 1;
        transform: translateY(-4px);
    }
}

.sipinor-ai-screen-badge {
    margin-top: 6px;
    font-size: 10px;
    opacity: 0.85;
    font-weight: 500;
}
.sipinor-ai-attach-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    justify-content: flex-end;
    margin-bottom: 6px;
    max-width: 220px;
    margin-left: auto;
}
.sipinor-ai-attach-strip--sent {
    justify-content: flex-start;
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 6px;
    max-width: 100%;
}
.sipinor-ai-attach-strip__hit {
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: zoom-in;
    border-radius: 7px;
    line-height: 0;
    flex-shrink: 0;
}
.sipinor-ai-attach-strip__hit:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.9);
    outline-offset: 2px;
}
.sipinor-ai-attach-strip__thumb {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border-radius: 7px;
    border: 1px solid rgba(255, 255, 255, 0.4);
    background: rgba(0, 0, 0, 0.15);
    flex-shrink: 0;
    vertical-align: middle;
}

/* Pending thumbnails live in the compose bar (Cursor-style) */
.sipinor-ai-compose__attachments {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 6px;
    padding-bottom: 8px;
    margin-bottom: 2px;
    border-bottom: 1px solid var(--sipinor-ai-border);
}
.sipinor-ai-compose__attach-item {
    position: relative;
    flex: 0 0 auto;
}
.sipinor-ai-compose__attach-hit {
    display: block;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    cursor: zoom-in;
    border-radius: 8px;
    line-height: 0;
}
.sipinor-ai-compose__attach-hit:focus-visible {
    outline: 2px solid var(--sipinor-ai-accent);
    outline-offset: 2px;
}
.sipinor-ai-compose__attach-thumb {
    width: 44px;
    height: 44px;
    object-fit: cover;
    border-radius: 8px;
    display: block;
    border: 1px solid var(--sipinor-ai-border);
    background: rgba(15, 23, 42, 0.04);
}
.sipinor-ai-panel--dark .sipinor-ai-compose__attach-thumb {
    background: rgba(248, 250, 252, 0.06);
}
.sipinor-ai-compose__attach-remove {
    position: absolute;
    top: -5px;
    right: -5px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: none;
    background: rgba(15, 23, 42, 0.78);
    color: #fff;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 9px;
    line-height: 1;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
}
.sipinor-ai-compose__attach-remove:hover {
    background: rgba(30, 41, 59, 0.92);
}
.sipinor-ai-panel--dark .sipinor-ai-compose__attach-remove {
    background: rgba(248, 250, 252, 0.2);
    color: #0f172a;
}
.sipinor-ai-panel--dark .sipinor-ai-compose__attach-remove:hover {
    background: rgba(248, 250, 252, 0.35);
}

.sipinor-ai-file-input {
    position: absolute;
    width: 0;
    height: 0;
    opacity: 0;
    pointer-events: none;
}

.sipinor-ai-compose {
    margin-top: 10px;
    flex-shrink: 0;
}
.sipinor-ai-compose__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding: 10px 10px 8px;
    border-radius: 14px;
    border: 1px solid var(--sipinor-ai-border);
    background: rgba(15, 23, 42, 0.02);
}
.sipinor-ai-panel--dark .sipinor-ai-compose__field {
    background: rgba(248, 250, 252, 0.04);
}
.sipinor-ai-textarea {
    width: 100%;
    border: none;
    background: transparent;
    resize: none;
    font-size: 13px;
    line-height: 1.5;
    color: inherit;
    min-height: 44px;
    max-height: 132px;
    padding: 4px 2px;
    outline: none;
}
.sipinor-ai-textarea::placeholder {
    color: var(--sipinor-ai-muted);
    opacity: 0.85;
}
.sipinor-ai-textarea:disabled {
    opacity: 0.55;
}
.sipinor-ai-compose__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}
.sipinor-ai-icon-btn {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    border: 1px solid var(--sipinor-ai-border);
    background: var(--sipinor-ai-surface);
    color: var(--sipinor-ai-muted);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.15s, color 0.15s;
}
.sipinor-ai-icon-btn:hover:not(:disabled) {
    border-color: var(--sipinor-ai-accent);
    color: var(--sipinor-ai-accent);
}
.sipinor-ai-panel--mode-action .sipinor-ai-icon-btn:hover:not(:disabled) {
    border-color: #14b8a6;
    color: #0f766e;
}
.sipinor-ai-panel--mode-action.sipinor-ai-panel--dark .sipinor-ai-icon-btn:hover:not(:disabled) {
    border-color: #2dd4bf;
    color: #5eead4;
}
.sipinor-ai-icon-btn:disabled {
    opacity: 0.45;
    cursor: not-allowed;
}
.sipinor-ai-send {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: 10px;
    padding: 8px 14px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    background: linear-gradient(135deg, var(--sipinor-ai-accent), var(--sipinor-ai-accent2));
    color: #fff;
    box-shadow: 0 2px 8px rgba(99, 102, 241, 0.35);
    transition: opacity 0.15s, transform 0.15s;
}
.sipinor-ai-send:hover:not(:disabled) {
    transform: translateY(-1px);
}
.sipinor-ai-send:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    transform: none;
}
.sipinor-ai-panel--mode-action .sipinor-ai-send {
    background: linear-gradient(135deg, #0d9488, #059669);
    box-shadow: 0 2px 8px rgba(13, 148, 136, 0.38);
}
.sipinor-ai-compose__kbd-hint {
    margin: 4px 4px 0;
    font-size: 10px;
    color: var(--sipinor-ai-muted);
    opacity: 0.9;
}

.sipinor-ai-fade-enter-active,
.sipinor-ai-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.22s cubic-bezier(0.22, 1, 0.36, 1);
}
.sipinor-ai-fade-enter,
.sipinor-ai-fade-leave-to {
    opacity: 0;
    transform: translateY(12px) scale(0.98);
}

/* Full-size attachment preview (above panel + fab) */
.sipinor-ai-lightbox {
    position: fixed;
    inset: 0;
    z-index: 1080;
    font-family: inherit;
}
.sipinor-ai-lightbox__backdrop {
    position: absolute;
    inset: 0;
    background: rgba(15, 23, 42, 0.78);
    cursor: pointer;
}
.sipinor-ai-lightbox__frame {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 1;
    max-width: min(96vw, 1400px);
    max-height: 92vh;
    padding: 8px;
    box-sizing: border-box;
}
.sipinor-ai-lightbox__img {
    display: block;
    max-width: min(calc(96vw - 16px), 1384px);
    max-height: calc(92vh - 16px);
    width: auto;
    height: auto;
    object-fit: contain;
    border-radius: 10px;
    box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
    background: #0f172a;
}
.sipinor-ai-lightbox__close {
    position: absolute;
    top: -4px;
    right: -4px;
    z-index: 2;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: rgba(15, 23, 42, 0.9);
    color: #f8fafc;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    transition: background 0.15s, transform 0.15s;
}
.sipinor-ai-lightbox__close:hover {
    background: #1e293b;
    transform: scale(1.05);
}
.sipinor-ai-lightbox__close .icon {
    font-size: 1.25rem;
}
</style>
<style>
.sipinor-ai-widget .sipinor-ai-msg__bubble--rich strong {
    font-weight: 700;
}
</style>
